import * as Accordion from '@radix-ui/react-accordion';
import { Trans, useTranslation } from 'react-i18next';

import { Tooltip } from '~/components';
import { chevron } from '~/components/DropdownTreeSingleSelect/styles';
import type {
  InternalDropdownOption,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface DropdownItemTriggerProps extends RadixUiProps {
  triggerRef: React.RefObject<HTMLButtonElement>;
  option: InternalDropdownOption;
}

export function DropdownItemTrigger({
  triggerRef,
  option,
  radix = {},
}: DropdownItemTriggerProps) {
  const { t } = useTranslation('common');

  const isOneOrMoreOptionsVisible = option.children?.some(
    (childOption) => childOption.isVisible,
  );

  /**
   * @TODO WMG1-737 Replace this Tooltip component with Radix UI's HoverCard component.
   * Tooltips are non-interactive overlays triggered by hover. HoverCards, on the other hand, are
   * interactive overlays also triggered by hover. (Popovers are interactive overlays triggered by click.)
   * The plan is to add a "Show nested options anyway" button inside the HoverCard that, when clicked,
   * will display the nested options even when they're !isVisible i.e. don't match the searchQuery.
   * This will allow users to narrow down and find the "category" they're looking for, and use that to then
   * access its nested/child options.
   */
  return (
    <Tooltip
      content={
        <Trans
          i18nKey="dropdownTree.noMatchingNestedOptions"
          t={t}
          values={{ count: option.children?.length }}
        />
      }
      size="sm"
      contentLayout="centered"
      triggerAsChild
      enabled={!isOneOrMoreOptionsVisible}
    >
      <div
        className={cn('mr-md rounded-full', chevron(), {
          'cursor-not-allowed hover:bg-emphasis-subtle':
            !isOneOrMoreOptionsVisible,
        })}
      >
        <Accordion.Trigger {...radix.accordionTrigger} asChild>
          <IconButton
            ref={triggerRef}
            type="button"
            iconType="chevron-down"
            size="small"
            className={cn(
              chevron({
                state: option.isExpanded ? 'open' : 'default',
              }),
              'transition-all',
            )}
            aria-expanded={option.isExpanded}
            aria-disabled={!isOneOrMoreOptionsVisible}
            aria-label={`Toggle ${option.label}`}
            disabled={!isOneOrMoreOptionsVisible}
          />
        </Accordion.Trigger>
      </div>
    </Tooltip>
  );
}

DropdownItemTrigger.displayName = 'DropdownItemTrigger';
